import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAdminAuth } from '@/contexts/AdminAuthContext';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback } from '@/components/ui/avatar';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Users, UserPlus, UserMinus, ShieldCheck, Calendar, Clock } from 'lucide-react';
import { format } from 'date-fns';
import { toast } from 'sonner';

interface Assignment {
  id: string;
  staff_user_id: string;
  assigned_at: string;
  assigned_by: string | null;
  notes: string | null;
  supervisor_email: string;
}

interface Supervisor {
  user_id: string;
  email: string;
}

interface PatientAssignmentsManagerProps {
  patientId: string;
  agencyId: string;
}

export function PatientAssignmentsManager({ patientId, agencyId }: PatientAssignmentsManagerProps) {
  const { user } = useAdminAuth();
  const [assignments, setAssignments] = useState<Assignment[]>([]);
  const [availableSupervisors, setAvailableSupervisors] = useState<Supervisor[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedSupervisor, setSelectedSupervisor] = useState<string>('');
  const [assignmentNotes, setAssignmentNotes] = useState('');
  const [isAssigning, setIsAssigning] = useState(false);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [revokingAssignment, setRevokingAssignment] = useState<Assignment | null>(null);
  const [isRevoking, setIsRevoking] = useState(false);

  const canManageAssignments = user?.role === 'super_admin' || user?.role === 'agency_admin';

  useEffect(() => {
    loadData();
  }, [patientId, agencyId]);

  const loadData = async () => {
    setLoading(true);
    try {
      await Promise.all([loadAssignments(), loadAvailableSupervisors()]);
    } finally {
      setLoading(false);
    }
  };

  const loadAssignments = async () => {
    try {
      // Get current assignments for this patient
      const { data: assignmentsData, error: assignmentsError } = await supabase
        .from('patient_assignments')
        .select('id, staff_user_id, assigned_at, assigned_by, notes')
        .eq('patient_id', patientId)
        .is('revoked_at', null);

      if (assignmentsError) throw assignmentsError;

      // Get supervisor emails for the assignments
      if (assignmentsData && assignmentsData.length > 0) {
        const userIds = assignmentsData.map(a => a.staff_user_id);
        const { data: profiles, error: profilesError } = await supabase
          .from('profiles')
          .select('user_id, email')
          .in('user_id', userIds);

        if (profilesError) throw profilesError;

        const enrichedAssignments = assignmentsData.map(a => ({
          ...a,
          supervisor_email: profiles?.find(p => p.user_id === a.staff_user_id)?.email || 'Unknown'
        }));

        setAssignments(enrichedAssignments);
      } else {
        setAssignments([]);
      }
    } catch (error) {
      console.error('Error loading assignments:', error);
      toast.error('Failed to load assignments');
    }
  };

  const loadAvailableSupervisors = async () => {
    try {
      // Get supervisors in the same agency
      const { data: profiles, error: profilesError } = await supabase
        .from('profiles')
        .select('user_id, email')
        .eq('agency_id', agencyId)
        .eq('is_active', true);

      if (profilesError) throw profilesError;

      // Get user_ids that have supervisor role
      const { data: roles, error: rolesError } = await supabase
        .from('user_roles')
        .select('user_id')
        .eq('role', 'supervisor');

      if (rolesError) throw rolesError;

      const supervisorUserIds = new Set(roles?.map(r => r.user_id) || []);
      const supervisors = (profiles || [])
        .filter(p => supervisorUserIds.has(p.user_id))
        .map(p => ({ user_id: p.user_id, email: p.email }));

      setAvailableSupervisors(supervisors);
    } catch (error) {
      console.error('Error loading supervisors:', error);
    }
  };

  const handleAssign = async () => {
    if (!selectedSupervisor || !user) return;

    setIsAssigning(true);
    try {
      const { error } = await supabase
        .from('patient_assignments')
        .insert({
          patient_id: patientId,
          staff_user_id: selectedSupervisor,
          assigned_by: user.id,
          notes: assignmentNotes || null
        });

      if (error) throw error;

      toast.success('Supervisor assigned to patient successfully');
      setShowAddDialog(false);
      setSelectedSupervisor('');
      setAssignmentNotes('');
      await loadAssignments();
    } catch (error: any) {
      console.error('Error assigning supervisor:', error);
      if (error.code === '23505') {
        toast.error('This supervisor is already assigned to this patient');
      } else {
        toast.error('Failed to assign supervisor');
      }
    } finally {
      setIsAssigning(false);
    }
  };

  const handleRevoke = async () => {
    if (!revokingAssignment) return;

    setIsRevoking(true);
    try {
      const { error } = await supabase
        .from('patient_assignments')
        .update({ revoked_at: new Date().toISOString() })
        .eq('id', revokingAssignment.id);

      if (error) throw error;

      toast.success('Assignment revoked successfully');
      setRevokingAssignment(null);
      await loadAssignments();
    } catch (error) {
      console.error('Error revoking assignment:', error);
      toast.error('Failed to revoke assignment');
    } finally {
      setIsRevoking(false);
    }
  };

  // Filter out already assigned supervisors
  const unassignedSupervisors = availableSupervisors.filter(
    s => !assignments.some(a => a.staff_user_id === s.user_id)
  );

  if (!canManageAssignments) {
    return null;
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Supervisor Assignments
              </CardTitle>
              <CardDescription>
                Manage which supervisors can access this patient's full information
              </CardDescription>
            </div>
            <Button 
              onClick={() => setShowAddDialog(true)}
              size="sm"
              disabled={unassignedSupervisors.length === 0}
            >
              <UserPlus className="h-4 w-4 mr-2" />
              Assign Supervisor
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">
              {[...Array(2)].map((_, i) => (
                <div key={i} className="h-16 bg-muted animate-pulse rounded-lg" />
              ))}
            </div>
          ) : assignments.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <ShieldCheck className="h-10 w-10 mx-auto mb-3 opacity-50" />
              <p className="font-medium">No supervisors assigned</p>
              <p className="text-sm mt-1">
                Assign supervisors to give them HIPAA-compliant access to this patient's data
              </p>
              {unassignedSupervisors.length > 0 && (
                <Button 
                  variant="outline" 
                  size="sm"
                  className="mt-4"
                  onClick={() => setShowAddDialog(true)}
                >
                  <UserPlus className="h-4 w-4 mr-2" />
                  Assign First Supervisor
                </Button>
              )}
              {unassignedSupervisors.length === 0 && availableSupervisors.length === 0 && (
                <p className="text-xs mt-4 text-amber-600">
                  No supervisors available in this agency. Add supervisors first.
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-3">
              {assignments.map((assignment) => (
                <div 
                  key={assignment.id} 
                  className="flex items-center justify-between p-4 rounded-lg border bg-card"
                >
                  <div className="flex items-center gap-3">
                    <Avatar className="h-10 w-10">
                      <AvatarFallback className="bg-primary/10 text-primary">
                        {assignment.supervisor_email.charAt(0).toUpperCase()}
                      </AvatarFallback>
                    </Avatar>
                    <div>
                      <div className="font-medium flex items-center gap-2">
                        <ShieldCheck className="h-4 w-4 text-primary" />
                        {assignment.supervisor_email}
                      </div>
                      <div className="flex items-center gap-3 text-xs text-muted-foreground mt-1">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Assigned {format(new Date(assignment.assigned_at), 'MMM d, yyyy')}
                        </span>
                        {assignment.notes && (
                          <span className="text-xs italic">"{assignment.notes}"</span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="text-destructive border-destructive/50 hover:bg-destructive/10"
                    onClick={() => setRevokingAssignment(assignment)}
                  >
                    <UserMinus className="h-4 w-4 mr-1" />
                    Revoke
                  </Button>
                </div>
              ))}
            </div>
          )}

          {/* Info about HIPAA masking */}
          <div className="mt-4 p-3 rounded-lg bg-muted/50 text-sm text-muted-foreground">
            <strong>HIPAA Compliance:</strong> Supervisors not assigned to this patient will see masked PII (name, email, DOB) when viewing patient records.
          </div>
        </CardContent>
      </Card>

      {/* Add Assignment Dialog */}
      <AlertDialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Assign Supervisor</AlertDialogTitle>
            <AlertDialogDescription>
              Select a supervisor to grant them access to this patient's full information.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label htmlFor="supervisor">Supervisor</Label>
              <Select value={selectedSupervisor} onValueChange={setSelectedSupervisor}>
                <SelectTrigger>
                  <SelectValue placeholder="Select a supervisor..." />
                </SelectTrigger>
                <SelectContent>
                  {unassignedSupervisors.map((supervisor) => (
                    <SelectItem key={supervisor.user_id} value={supervisor.user_id}>
                      {supervisor.email}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="notes">Notes (optional)</Label>
              <Textarea
                id="notes"
                placeholder="Add any notes about this assignment..."
                value={assignmentNotes}
                onChange={(e) => setAssignmentNotes(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isAssigning}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleAssign}
              disabled={!selectedSupervisor || isAssigning}
            >
              {isAssigning ? 'Assigning...' : 'Assign Supervisor'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Revoke Confirmation Dialog */}
      <AlertDialog open={!!revokingAssignment} onOpenChange={(open) => !open && setRevokingAssignment(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Revoke Assignment</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to revoke access for <span className="font-semibold text-foreground">{revokingAssignment?.supervisor_email}</span>? 
              They will no longer be able to see this patient's full information.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isRevoking}>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleRevoke}
              disabled={isRevoking}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {isRevoking ? 'Revoking...' : 'Revoke Access'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
